import React, { useState } from 'react';
import { Stack, Group, TextInput, Button, Badge, ActionIcon, Text, Loader } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useTags } from '../../hooks/useReferenceApi';
import { useCreateTag, useDeleteTag } from '../../hooks/useTagApi';
import { getErrorMessage } from '../../utils/api';

export const TagsManagementPanel: React.FC = () => {
  const { t } = useTranslation();
  const { data: tags, isLoading } = useTags();
  const createTag = useCreateTag();
  const deleteTag = useDeleteTag();
  const [name, setName] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!name.trim()) return;
    try {
      await createTag.mutateAsync(name.trim());
      setName('');
      notifications.show({ color: 'teal', message: t('tagsManagement.created') });
    } catch (e) {
      notifications.show({ color: 'red', message: getErrorMessage(e) });
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteTag.mutateAsync(id);
      notifications.show({ color: 'teal', message: t('tagsManagement.deleted') });
    } catch (e) {
      notifications.show({ color: 'red', message: getErrorMessage(e) });
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDelete = (id: string, tagName: string) => {
    modals.openConfirmModal({
      title: t('tagsManagement.deleteConfirm', { name: tagName }),
      children: t('tagsManagement.deleteConfirmBody'),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => handleDelete(id),
    });
  };

  if (isLoading) return <Loader size="sm" />;

  return (
    <Stack gap="md">
      <Group>
        <TextInput
          placeholder={t('tagsManagement.addPlaceholder')}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button onClick={handleAdd} loading={createTag.isPending}>
          {t('tagsManagement.add')}
        </Button>
      </Group>
      {(!tags || tags.length === 0) && <Text c="dimmed">{t('tagsManagement.empty')}</Text>}
      <Group gap="xs">
        {tags?.map((tag) => (
          <Badge
            key={tag._id}
            variant="light"
            rightSection={
              <ActionIcon
                size="xs"
                variant="transparent"
                color="gray"
                loading={deletingId === tag._id}
                disabled={deletingId !== null}
                onClick={() => confirmDelete(tag._id, tag.name)}
              >
                <IconTrash size={12} />
              </ActionIcon>
            }
          >
            {tag.name}
          </Badge>
        ))}
      </Group>
    </Stack>
  );
};
